import { ParkingHistory } from '@parkingbase/ecommerce-api-dev';

import { create } from 'zustand';
import { ParkingHistoryService } from '@/services/parking-history.service';
import { downloadBlob } from '@/lib/file-download';
import { logger } from '@/lib/logger';

const parkingHistoryService = new ParkingHistoryService();

interface ParkingHistoryState {
  transientHistory: ParkingHistory[];
  monthlyHistory: ParkingHistory[];
  isLoadingTransient: boolean;
  isLoadingMonthly: boolean;
  error: string | null;
  fetchTransientHistory: () => Promise<void>;
  fetchMonthlyHistory: () => Promise<void>;
  downloadTransientReceipt: (parkingHistoryId: string) => Promise<void>;
  downloadMonthlyReceipt: (company: string, transactionId: string) => Promise<void>;
  reset: () => void;
}

export const useParkingHistoryStore = create<ParkingHistoryState>((set) => ({
  transientHistory: [],
  monthlyHistory: [],
  isLoadingTransient: false,
  isLoadingMonthly: false,
  error: null,

  reset: () =>
    set({
      transientHistory: [],
      monthlyHistory: [],
      isLoadingTransient: false,
      isLoadingMonthly: false,
      error: null,
    }),

  fetchTransientHistory: async () => {
    try {
      set({ isLoadingTransient: true, error: null });
      const data = await parkingHistoryService.getParkingHistory();
      set({ transientHistory: data || [] });
    } catch (error) {
      set({ error: 'failedToFetchTransientHistory' });
      logger.error('Failed to fetch transient parking history', error, {
        feature: 'parking-history-store',
        route: 'fetchTransientHistory',
      });
    } finally {
      set({ isLoadingTransient: false });
    }
  },

  fetchMonthlyHistory: async () => {
    try {
      set({ isLoadingMonthly: true, error: null });
      const data = await parkingHistoryService.getMonthlyHistory();
      set({ monthlyHistory: data || [] });
    } catch (error) {
      set({ error: 'failedToFetchMonthlyHistory' });
      logger.error('Failed to fetch monthly parking history', error, {
        feature: 'parking-history-store',
        route: 'fetchMonthlyHistory',
      });
    } finally {
      set({ isLoadingMonthly: false });
    }
  },

  downloadTransientReceipt: async (parkingHistoryId: string) => {
    try {
      const fileData = await parkingHistoryService.getTransientReceipt(parkingHistoryId);
      downloadBlob(fileData, `receipt-${parkingHistoryId}.pdf`);
    } catch (error) {
      logger.error('Failed to download transient receipt', error, {
        feature: 'parking-history-store',
        route: 'downloadTransientReceipt',
      });
    }
  },

  downloadMonthlyReceipt: async (company: string, transactionId: string) => {
    try {
      const fileData = await parkingHistoryService.getMonthlyReceipt(company, transactionId);
      downloadBlob(fileData, `receipt-monthly-${transactionId}.pdf`);
    } catch (error) {
      logger.error('Failed to download monthly receipt', error, {
        feature: 'parking-history-store',
        route: 'downloadMonthlyReceipt',
      });
    }
  },

}));
